import { useMemo, useState } from 'react';

interface IUseRowSelectionProps<TItem, TId extends string | number> {
  items: TItem[];
  getId: (item: TItem) => TId;
  isSelectable?: (item: TItem) => boolean;
}

export function useRowSelection<TItem, TId extends string | number>(props: IUseRowSelectionProps<TItem, TId>) {
  const { items, getId, isSelectable } = props;
  const [selectedIds, setSelectedIds] = useState<TId[]>([]);

  const selectableItems = useMemo(() => {
    if (!isSelectable) {
      return items;
    }

    return items.filter((item) => isSelectable(item));
  }, [items, isSelectable]);

  const isAllSelected = selectableItems.length > 0 && selectableItems.every((item) => selectedIds.includes(getId(item)));

  const toggleItem = (id: TId, checked: boolean) => {
    if (checked) {
      setSelectedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      return;
    }

    setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
  };

  const toggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(selectableItems.map((item) => getId(item)));
      return;
    }

    setSelectedIds([]);
  };

  const resetSelection = () => {
    setSelectedIds([]);
  };

  return {
    selectedIds,
    isAllSelected,
    toggleItem,
    toggleAll,
    resetSelection,
  };
}
